import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { trigger, style, animate, transition } from '@angular/animations';
import { combineLatest, map } from 'rxjs';

import { AuthService } from '../../../core/services/auth.service';
import { Permission, PERMISSIONS_BY_ROLE } from '../../../core/auth/permissions';
import { ERole, normalizeRole } from '../../../core/auth/roles';
import { MENU, MenuItem } from '../../topbarmenu/menu-items';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-mobile-drawer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './mobile-drawer.component.html',
  styleUrls: ['./mobile-drawer.component.scss'],
  animations: [
    trigger('drawerAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))]),
    ]),
  ],
})
export class MobileDrawerComponent {
  @Output() close = new EventEmitter<void>();
  private auth = inject(AuthService);

  vm$ = combineLatest([this.auth.isAuthenticated$, this.auth.userProfile$]).pipe(
    map(([isAuth, profile]) => {
      const role = normalizeRole(profile?.role ?? null);

      const items = (
        isAuth
          ? MENU.filter((i) => {
              if (i.guestOnly) return false;
              if (i.onlyRoles?.length && (!role || !i.onlyRoles.includes(role))) return false;
              if (!i.anyPerm?.length) return true;
              return this.hasAnyPermSync(role, i.anyPerm);
            })
          : MENU.filter((i) => i.guestOnly)
      )
        .sort((a, b) => this.getOrder(a, role) - this.getOrder(b, role))
        .map((i) => this.withRoleLabel(i, role));

      return { isAuth, items };
    }),
  );

  private hasAnyPermSync(role: ERole | null, perms: Permission[]): boolean {
    if (!role) return false;
    const granted = PERMISSIONS_BY_ROLE[role];
    return perms.some((p) => granted.has(p));
  }

  private getOrder(item: MenuItem, role: ERole | null): number {
    if (role && item.orderByRole && item.orderByRole[role] !== undefined) {
      return item.orderByRole[role]!;
    }
    return item.order ?? 1000;
  }

  private withRoleLabel(item: MenuItem, role: ERole | null): MenuItem {
    const lbl = role ? item.labelByRole?.[role] : undefined;
    return lbl ? { ...item, label: lbl } : item;
  }

  trackByRoute = (_: number, it: MenuItem) => it.route;

  logout(): void {
    if (environment.features?.redirectLogin) {
      this.close.emit();
      this.auth.logoutRedirect();
      return;
    }
    this.auth.logout().subscribe(() => this.close.emit());
  }
}
